<template>
    <span class="password-reset-text-box">
        <b-input-group>
            <b-input-group-text
                v-show="indicatorText !== ''"
                slot="append"
                :class="'password-strength-indicator ' + indicatorClass"
                @click="clickStrengthIndicator()"
            >
                {{ indicatorText }}
            </b-input-group-text>
            <b-form-input
                ref="passwordInput"
                :id="inputId"
                v-model="password"
                :maxlength="maxlength"
                :disabled="disabled"
                :name="name"
                :placeholder="placeholder"
                :required="required"
                :state="null"
                :aria-label="ariaLabel"
                type="password"
            />
        </b-input-group>
    </span>
</template>

<script>
import PgbaBaseComponent from './PgbaBaseComponent.vue';
import {isBlank} from '../../util/StringUtils';
export default {
    name: 'PasswordTextBox',
    extends: PgbaBaseComponent,
    props: {
        maxlength: {
            type: String,
            required: false,
            default: '15'
        },
        minlength: {
            type: String,
            required: false,
            default: '8'
        },
        ariaLabel: {
            type: String,
            required: false,
            default: ''
        },
        disabled: {
            type: Boolean,
            required: false,
            default: false
        },
        id: {
            type: String,
            required: false,
            default: ''
        },
        name: {
            type: String,
            required: false,
            default: ''
        },
        placeholder: {
            type: String,
            required: false,
            default: ''
        },
        required: {
            type: Boolean,
            required: false,
            default: false
        },
        value: {
            type: String,
            required: false,
            default: ''
        },
        errorFieldName: {
            type: String,
            required: false,
            default: 'Password'
        },
        valueToConfirm: {
            type: String,
            required: false,
            default: null
        }
    },
    data() {
        return {
            password: '',
            indicatorClass: '',
            indicatorText: ''
        };
    },
    computed: {
        minPasswordLength() {
            return parseInt(this.minlength);
        },
        maxPasswordLength() {
            return parseInt(this.maxlength);
        },
        inputId() {
            return (isBlank(this.id) ? this.$id(this.name) : this.id) + '-input';
        },
        isInvalid() {
            return (this.indicatorClass === 'invalid');
        }
    },
    watch: {
        password() {
            if (this.password !== '') {
                this.updateStrengthIndicator();
            } else {
                this.indicatorClass = '';
                this.indicatorText = '';
            }
            this.$emit('input', this.password);
        }
    },
    mounted() {
        this.password = this.value;
    },
    methods: {

        /** Focus on the input if clicked */
        clickStrengthIndicator() {
            this.$refs.passwordInput.focus();
        },

        updateStrengthIndicator() {
            this.indicatorStatus = '';
            // I'm the "confirm" version of the password entry fields
            if (this.valueToConfirm) {
                var myValue = this.password;
                var otherValue = this.valueToConfirm;
                if (myValue == '') {
                    // ""
                    this.indicatorClass = 'invalid';
                    this.indicatorText = 'Invalid';
                } else if (myValue != otherValue) {
                    // "No Match"
                    this.indicatorClass = 'invalid';
                    this.indicatorText = 'No Match';
                } else {
                    // "Match"
                    this.indicatorClass = 'good';
                    this.indicatorText = 'Match';
                }
            } else {
                var value = this.password;
                var goodThreshold = Math.min(
                    Math.floor(this.minPasswordLength * 1.25),
                    this.maxPasswordLength
                );
                if (!this.isValid()) {
                    // "Invalid" or "Short"
                    this.indicatorClass = 'invalid';
                    if (value.length > 0 && value.length < this.minPasswordLength) { // Too Short
                        this.indicatorText = 'Short';
                    } else { // Otherwise Invalid
                        this.indicatorText = 'Invalid';
                    }
                } else if (value.length < goodThreshold) {
                    // "Fair"
                    this.indicatorClass = 'fair';
                    this.indicatorText = 'Fair';
                } else {
                    // "Strong"
                    this.indicatorClass = 'good';
                    this.indicatorText = 'Strong';
                }
            }
        },

        isValid: function() {
            var value = this.password;
            var isValid = true;
            if (value.length < this.minPasswordLength || value.length > this.maxPasswordLength) {
                // Too long or short
                isValid = false;
            } else if (this.containsRepeatingCharacters()) {
                // check for repeating characters -- regex: \([a-zA-Z0-9]\)\1
                isValid = false;
            } else if (this.containsConsecutiveCharacters()) {
                // check for consecutive characters (ex. 123, ABC)
                isValid = false;
            } else if ( !this.containsLetter() ) {
                // check for letter
                isValid = false;
            } else if ( !this.containsNumber() ) {
                // check for number
                isValid = false;
            } else if ( !this.containsSpecialCharacter() ) {
                // check for special character @ # or $
                isValid = false;
            } else if (this.containsIllegalSpecialCharacter() ) {
                isValid = false;
            }
            return isValid;
        },

        isTooShort: function() {
            return this.password.length > 0 && this.textBoxNode.value.length < this.minPasswordLength;
        },

        containsLetter: function() {
            return this.password.match(/[a-zA-Z]/);
        },

        containsNumber: function() {
            return this.password.match(/\d/);
        },

        containsSpecialCharacter: function() {
            return this.password.match(/[@#$]/);
        },

        containsIllegalSpecialCharacter: function() {
            // eslint-disable-next-line
            return /[~`!%^&*()\-\+={}\[\]\\|:;<>,./\?\"\'_]/.test(this.password);
        },

        containsRepeatingCharacters: function() {
            return this.password.match(/([a-zA-Z0-9@#$])\1\1/);
        },

        containsConsecutiveCharacters: function() {

            var value = this.password;

            for ( var x = 0; x < value.length-2; x++ ) {
                if ( ( value.charCodeAt(x+1) == value.charCodeAt(x) + 1 )
                  && ( value.charCodeAt(x+2) == value.charCodeAt(x+1) + 1 ) ) {
                    return -1;
                } else if ( ( value.charCodeAt(x+1) == value.charCodeAt(x) - 1 )
                         && ( value.charCodeAt(x+2) == value.charCodeAt(x+1) - 1 ) ) {
                    return -1;
                }
            }
            return 0;
        },

        getErrorMessage() {
            if (this.isValid()) return '';

            if (!this.errorFieldName) {
                this.errorFieldName = "Password";
            }
            if (this.textBoxNode.value.length == 0) {
                return this.errorMessage;
            }
            if (this.containsRepeatingCharacters()) {
                return `<b>${this.errorFieldName}:</b>
                    Please enter a password with no more than two of the same characters in a row.
                    Not allowed: 'aaa', '111'.`;
            }
            if (this.containsConsecutiveCharacters()) {
                return `<b>${this.errorFieldName}:</b>
                    Please enter a password with no more than two of the same sequential characters in a row.
                    Not allowed: 'abc', '123'.`;
            }
            if (this.isTooShort()) {
                return `<b>${this.errorFieldName}:</b> Please enter a password with at least eight characters.`;
            }
            if (!this.containsLetter()) {
                return `<b>${this.errorFieldName}:</b> Please enter a password with at least one letter.`;
            }
            if (!this.containsNumber()) {
                return `<b>${this.errorFieldName}:</b> Please enter a password with at least one number.`;
            }
            if (!this.containsSpecialCharacter()) {
                return `<b>${this.errorFieldName}:</b>
                    Please enter a password with at least one of the following special characters: @, #, $.`;
            }
            if (this.containsIllegalSpecialCharacter()) {
                return `<b>${this.errorFieldName}:</b> Special characters are not allowed.`;
            }
            return this.errorMessage;
        },

        clear() {
            this.password = '';
        }
    }
};
</script>

<style>
.password-reset-text-box .password-strength-indicator {
    height: initial;
    line-height: initial;
}
</style>
